/**
 * `React.lazy` that survives a deploy happening under an open tab.
 *
 * THE FAILURE. Vite emits content-hashed chunks (`Pipeline-DLYfe5aM.js`), and
 * `netlify.toml` rewrites `/*` to `/index.html` with **status 200**. So after a
 * deploy, a tab still running the previous `index-*.js` asks for a chunk filename
 * that no longer exists, and instead of a 404 it receives `index.html` with a 200.
 * The browser tries to evaluate HTML as an ES module, the dynamic import rejects,
 * and `lazy()` throws during render — a blank screen. Reloading fetches the new
 * index, which is why the second attempt always works.
 *
 * THE FIX. Retry once, in case the failure was merely transient. If it fails
 * again, treat it as a stale deploy and reload the page once — that is the only
 * thing that can help, since the chunk this build is asking for is genuinely gone.
 *
 * The reload is recorded in sessionStorage and only ever performed once per
 * session, so a chunk that is broken for some other reason surfaces as a real
 * error to the nearest error boundary instead of reloading forever.
 */

import { lazy, type ComponentType } from "react";

/**
 * `any` mirrors React's own `lazy<T extends ComponentType<any>>`. Narrowing it to
 * `unknown` makes every component whose props are not exactly `{}` unassignable,
 * which rejects most real call sites.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComponent = ComponentType<any>;

type Importer<T> = () => Promise<{ default: T }>;

const RELOAD_FLAG = "chunk-reload-attempted";

/** Errors browsers produce when a module URL serves something that is not a module. */
const STALE_CHUNK_PATTERNS = [
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "expected a javascript-or-wasm module",
  "expected a javascript module",
  "'text/html' is not a valid javascript mime type",
  "importing a module script failed",
  "unexpected token '<'",
];

export function isStaleChunkError(error: unknown): boolean {
  const message =
    typeof error === "string"
      ? error
      : error instanceof Error
        ? `${error.name}: ${error.message}`
        : "";

  const haystack = message.toLowerCase();
  return STALE_CHUNK_PATTERNS.some((pattern) => haystack.includes(pattern));
}

/** Whether a reload has already been spent this session. */
export function hasAttemptedReload(
  storage: Pick<Storage, "getItem"> | undefined = safeSessionStorage()
): boolean {
  try {
    return storage?.getItem(RELOAD_FLAG) === "1";
  } catch {
    return false;
  }
}

function safeSessionStorage(): Storage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.sessionStorage;
  } catch {
    // Storage can throw in private modes and sandboxed frames.
    return undefined;
  }
}

/**
 * Decides what to do about a failed chunk import. Kept pure and exported so the
 * policy is testable without a browser: the caller performs the effect.
 *
 * - `retry`   — first failure; try the import again
 * - `reload`  — looks like a stale deploy and no reload spent yet
 * - `rethrow` — anything else, so a real error reaches an error boundary
 */
export function decideChunkFailureAction(
  error: unknown,
  { attempt, reloadAlreadyAttempted }: { attempt: number; reloadAlreadyAttempted: boolean }
): "retry" | "reload" | "rethrow" {
  if (attempt === 0) return "retry";
  if (isStaleChunkError(error) && !reloadAlreadyAttempted) return "reload";
  return "rethrow";
}

/**
 * Drop-in replacement for `lazy()`.
 *
 * @param importer the same `() => import("...")` you would pass to lazy()
 */
export function lazyWithRetry<T extends AnyComponent>(
  importer: Importer<T>
) {
  return lazy(async () => {
    let attempt = 0;

    // Two passes at most: the original call and one retry.
    for (;;) {
      try {
        const module = await importer();

        // A successful load means this build's chunks are reachable, so a future
        // failure in this session deserves its own reload allowance.
        try {
          safeSessionStorage()?.removeItem(RELOAD_FLAG);
        } catch {
          // Not being able to clear the flag is harmless.
        }

        return module;
      } catch (error) {
        const action = decideChunkFailureAction(error, {
          attempt,
          reloadAlreadyAttempted: hasAttemptedReload(),
        });

        if (action === "retry") {
          attempt += 1;
          continue;
        }

        if (action === "reload") {
          try {
            safeSessionStorage()?.setItem(RELOAD_FLAG, "1");
          } catch {
            // If we cannot record the attempt, rethrow rather than risk a loop.
            throw error;
          }
          window.location.reload();

          // Keep the promise pending while the reload happens, so React does not
          // render an error state during teardown.
          return new Promise<{ default: T }>(() => {});
        }

        throw error;
      }
    }
  });
}
